import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../components/LoadingSpinner";


const EditSalaryReport = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem("authToken");

  const [salary, setSalary] = useState(null);
  const [otherAllowances, setOtherAllowances] = useState([]);
  const [otherDeductions, setOtherDeductions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Initialize as empty arrays to prevent map errors
  const [otherAllowanceList, setOtherAllowanceList] = useState([]);
  const [otherDeductionList, setOtherDeductionList] = useState([]);

  const operation = id ? "Edit" : "Add";
  const managePage = `${operation} Salary for ${salary?.user_fullname || ''} - ${salary?.monthYear || ''}`;

  useEffect(() => {
    if (id) {
      fetchSalaryData();
    }
  }, [id]);

  const fetchSalaryData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}payroll/salary-report/edit-payslip/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        const data = response.data;
        console.log("Fetched salary data:", data); // Debug log

        setSalary(data.salary);

        // Ensure arrays are properly set
        setOtherAllowances(Array.isArray(data.other_allowance) ? data.other_allowance : []);
        setOtherDeductions(Array.isArray(data.other_deduction) ? data.other_deduction : []);

        // Initialize lists from salary data, ensure they are arrays
        const allowanceList = data.salary?.other_allowance_json;
        const deductionList = data.salary?.other_deduction_json;

        setOtherAllowanceList(Array.isArray(allowanceList) ? allowanceList : []);
        setOtherDeductionList(Array.isArray(deductionList) ? deductionList : []);

        console.log("Other allowance list:", allowanceList); // Debug log
        console.log("Other deduction list:", deductionList); // Debug log
      }
    } catch (error) {
      console.error("Error fetching salary data:", error);
      showToast.error("Failed to fetch salary data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        id,
        other_allowances: otherAllowanceList,
        other_deductions: otherDeductionList
      };

      const response = await axios.put(
        `${API_URL}payroll/salary-report/update-payslip`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 1 ||
        response.data.status === 200 ||
        response.data.status === 201 ||
        response.data.success === true ||
        response.status === 200) {

        showToast.success(response.data.message || "Payslip updated successfully!");
        navigate("/Payroll/SalaryReport");
      } else {
        showToast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error updating salary:", error);
      showToast.error(error.response?.data?.message || "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const addOtherAllowance = () => {
    setOtherAllowanceList(prevList => [...prevList, { id: "", amount: "" }]);
  };

  const addOtherDeduction = () => {
    setOtherDeductionList(prevList => [...prevList, { id: "", amount: "" }]);
  };

  const handleAllowanceChange = (index, field, value) => {
    setOtherAllowanceList(prevList => {
      const updated = [...prevList];
      if (updated[index]) {
        updated[index][field] = value;
      }
      return updated;
    });
  };

  const handleDeductionChange = (index, field, value) => {
    setOtherDeductionList(prevList => {
      const updated = [...prevList];
      if (updated[index]) {
        updated[index][field] = value;
      }
      return updated;
    });
  };

  const removeRow = (index, type) => {
    if (type === "allowance") {
      setOtherAllowanceList(prevList => prevList.filter((_, i) => i !== index));
    } else {
      setOtherDeductionList(prevList => prevList.filter((_, i) => i !== index));
    }
  };

  // Show loading state while fetching data
  if (loading && !salary) {
    return (
      <LoadingSpinner></LoadingSpinner>
    );
  }

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        {loading && (
          <div className="overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}>
            <div className="spinner-border text-light" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        <div className="row pb-2">
          <div className="col-md-6">
            <ul className="breadcrumb">
              <li className="breadcrumb-item active">{managePage}</li>
            </ul>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <button
              className="btn btn-default btn-sm"
              onClick={() => navigate("/Payroll/SalaryReport")}
            >
              <b><FontAwesomeIcon icon={faChevronLeft} /></b> Return to Salary Report
            </button>
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading">
            <span className="panel-title">{managePage}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="stdform">
          <input type="hidden" name="id" value={id} />

          <div className="row">
            <div className="col-6 mt-3">
              <div className="panel panel-default with-rounded-corner with-shadow">
                <div className="panel-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="control-label mb-0">Other Allowance</label>
                    <button
                      type="button"
                      className="addOtherAllowance btn btn-success btn-xs col-md-2"
                      onClick={addOtherAllowance}
                    >
                      Add New
                    </button>
                  </div>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center", width: "40%" }}>
                          <div className="th-inner">Allowance</div>
                        </th>
                        <th style={{ textAlign: "center", width: "40%" }}>
                          <div className="th-inner">Amount</div>
                        </th>
                        <th style={{ textAlign: "center", width: "20%" }}>
                          <div className="th-inner">Delete</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="other_allowance">
                      {Array.isArray(otherAllowanceList) && otherAllowanceList.length > 0 ? (
                        otherAllowanceList.map((item, index) => (
                          <tr key={`allowance-${index}`}>
                            <td>
                              <select
                                name="other_allowance_id[]"
                                className="form-control"
                                value={item.id || ""}
                                onChange={(e) => handleAllowanceChange(index, "id", e.target.value)}
                                required
                              >
                                <option value="">Select Allowance</option>
                                {Array.isArray(otherAllowances) && otherAllowances.map(allowance => (
                                  <option key={allowance.id} value={allowance.id}>
                                    {allowance.title}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="number"
                                name="other_allowance_amount[]"
                                value={item.amount || ""}
                                onChange={(e) => handleAllowanceChange(index, "amount", e.target.value)}
                                placeholder="Enter Amount"
                                className="form-control"
                                required
                              />
                            </td>
                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm delete_row"
                                onClick={() => removeRow(index, "allowance")}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center">
                            No allowances added. Click "Add New" to add an allowance.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-6 mt-3">
              <div className="panel panel-default with-rounded-corner with-shadow">
                <div className="panel-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <label className="control-label mb-0">Other Deduction</label>
                    <button
                      type="button"
                      className="addOtherDeduction btn btn-success btn-xs col-md-2"
                      onClick={addOtherDeduction}
                    >
                      Add New
                    </button>
                  </div>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center", width: "40%" }}>
                          <div className="th-inner">Deduction</div>
                        </th>
                        <th style={{ textAlign: "center", width: "40%" }}>
                          <div className="th-inner">Amount</div>
                        </th>
                        <th style={{ textAlign: "center", width: "20%" }}>
                          <div className="th-inner">Delete</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="other_deduction">
                      {Array.isArray(otherDeductionList) && otherDeductionList.length > 0 ? (
                        otherDeductionList.map((item, index) => (
                          <tr key={`deduction-${index}`}>
                            <td>
                              <select
                                name="other_deduction_id[]"
                                className="form-control"
                                value={item.id || ""}
                                onChange={(e) => handleDeductionChange(index, "id", e.target.value)}
                                required
                              >
                                <option value="">Select Deduction</option>
                                {Array.isArray(otherDeductions) && otherDeductions.map(deduction => (
                                  <option key={deduction.id} value={deduction.id}>
                                    {deduction.title}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="number"
                                name="other_deduction_amount[]"
                                value={item.amount || ""}
                                onChange={(e) => handleDeductionChange(index, "amount", e.target.value)}
                                placeholder="Enter Amount"
                                className="form-control"
                                required
                              />
                            </td>
                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm delete_row"
                                onClick={() => removeRow(index, "deduction")}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center">
                            No deductions added. Click "Add New" to add a deduction.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="clearfix"></div>
          <div className="mt-3">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Processing..." : "Submit"}
            </button>
            <button
              type="button"
              className="btn btn-default ml-2 ms-2"
              onClick={() => navigate("/Payroll/SalaryReport")}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSalaryReport;
